import { captureDiscussionDraft, clearStoredDiscussionDraft } from './discussion-form-state.js';
import { initDiscussionPreview } from './article-ui.js';
import { Tooltip } from 'flowbite';
import { showAjaxFlash } from './page-ui.js';

/* =================================================================
   CLASICO FEED UI
   Všechno, co patří přímo k feedu:
   - komentáře a odpovědi
   - @mention
   - smooth open/close komentářů
   - AJAX formuláře diskuze
   - composer nového příspěvku
   - ankety a countdown
   - srdíčka
   - moderace
   - připínání a lokální řazení feedu
================================================================= */

export function initFeedUI(root = document) {
    initCommentReplyUI(root);
    initNotificationDeepLink(root);
    initLazyPostComments(root);
    initDiscussionTextareas(root);
    initDiscussionFormsAjax(root);
    initDiscussionPreview(root);
    initCreatePostModalController(root);
    initCreatePostComposer(root);
    initPollVoting(root);
    initPollCountdowns(root);
    initHeartReactions(root);
    initContentModeration(root);
}

/* =================================================================
   CREATE POST MODAL
   Vlastní malý controller. Tento konkrétní modal záměrně neřídí
   Flowbite auto-init, protože initFlowbite() se po Naja requestech
   spouští opakovaně a mohl držet stale/duplicitní modal instance.
================================================================= */
function initCreatePostModalController() {
    if (window.__clasicoCreatePostModalControllerBound) return;
    window.__clasicoCreatePostModalControllerBound = true;

    const getModal = () => document.getElementById('create-post-modal');

    const cleanupLegacyFlowbiteState = () => {
        /*
         * Create-post modal má vlastní controller. Starší/mobile trigger ale
         * mohl Flowbite nechat vytvořit backdrop. Ten po AJAXu blokuje celou
         * stránku, i když je náš modal už zavřený.
         */
        document.querySelectorAll('[modal-backdrop]').forEach(backdrop => backdrop.remove());
    };

    const open = () => {
        const modal = getModal();
        if (!modal) return;

        cleanupLegacyFlowbiteState();
        modal.classList.remove('hidden');
        modal.classList.add('flex');
        modal.setAttribute('aria-hidden', 'false');
        document.body.classList.add('overflow-hidden');
    };

    const close = () => {
        const modal = getModal();
        if (!modal) return;

        modal.classList.add('hidden');
        modal.classList.remove('flex');
        modal.setAttribute('aria-hidden', 'true');
        cleanupLegacyFlowbiteState();
        document.body.classList.remove('overflow-hidden');
    };

    window.__clasicoOpenCreatePostModal = open;
    window.__clasicoCloseCreatePostModal = close;

    document.addEventListener('click', event => {
        const openTrigger = event.target.closest('[data-create-post-modal-open]');
        if (openTrigger) {
            if (openTrigger.dataset.createPostTypeDisabled === '1') return;
            event.preventDefault();
            open();
            return;
        }

        const closeTrigger = event.target.closest('[data-create-post-modal-close]');
        if (closeTrigger) {
            event.preventDefault();
            close();
            return;
        }

        const modal = getModal();
        if (modal && event.target === modal && !modal.classList.contains('hidden')) {
            close();
        }
    });

    document.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;

        const modal = getModal();
        if (!modal || modal.classList.contains('hidden')) return;

        event.preventDefault();
        close();
    });
}

/* =================================================================
   KOMENTÁŘE VE FEEDU
   Kompaktní Facebook-style odpověď bez reloadu stránky.
================================================================= */
function initCommentReplyUI(root = document) {
    root.querySelectorAll('[data-comment-reply-focus]').forEach(button => {
        if (button.dataset.replyInitialized === '1') return;
        button.dataset.replyInitialized = '1';

        button.addEventListener('click', () => {
            const postId = button.dataset.commentReplyFocus;
            const label = button.dataset.replyLabel || '';
            const rawMention = button.dataset.replyMention || '';

            const composer = document.querySelector(
                `[data-comment-composer="${postId}"]`
            );

            if (!composer) return;

            const replyCommentId = Number(button.dataset.replyCommentId || 0);
            const replyField = composer.form?.elements?.namedItem?.('reply_to_comment_id');
            if (replyField && replyCommentId > 0) {
                replyField.value = String(replyCommentId);
            }

            const mentionName = rawMention
                .trim()
                .replace(/^@+/, '')
                .replace(/\s+/g, '');

            const mention = mentionName ? `@${mentionName}` : '';

            if (mention) {
                const current = composer.value || '';
                const mentionAtStart = current.match(/^@\S+\s*/u);

                if (mentionAtStart) {
                    composer.value =
                        `${mention} ${current.slice(mentionAtStart[0].length)}`;
                } else if (current.trim() === '') {
                    composer.value = `${mention} `;
                } else {
                    composer.value = `${mention} ${current}`;
                }

                composer.dispatchEvent(
                    new Event('input', { bubbles: true })
                );
            }

            composer.placeholder = label
                ? `Odpovědět uživateli ${label}…`
                : 'Napište komentář…';

            composer.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });

            window.requestAnimationFrame(() => {
                composer.focus();

                const end = composer.value.length;
                composer.setSelectionRange?.(end, end);
            });
        });
    });
}


/* =================================================================
   NOTIFICATION DEEP LINK
   /?openComments=123#comment-456 otevře AJAX komentáře a po načtení
   skočí přímo na konkrétní komentář.
================================================================= */
function initNotificationDeepLink() {
    if (window.__clasicoNotificationDeepLinkBound) return;
    window.__clasicoNotificationDeepLinkBound = true;

    const params = new URLSearchParams(window.location.search);
    const postId = params.get('openComments') || document.querySelector('[data-post-detail-id]')?.dataset.postDetailId;
    if (!postId || !/^\d+$/.test(postId)) return;

    const toggle = document.querySelector(`[data-comments-toggle="${postId}"]`);
    const host = document.querySelector(`[data-comments-host="${postId}"]`);
    const post = document.getElementById(`post-${postId}`);

    post?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    if (host?.dataset.loaded === '1') {
        if (host.classList.contains('hidden') || host.dataset.commentsCollapsed === '1') {
            setCommentsHostOpen(host, true);
        }
        scrollToNotificationComment(postId);
        return;
    }

    window.setTimeout(() => toggle?.click(), 80);
}

function scrollToNotificationComment(postId) {
    const hash = window.location.hash || '';
    const match = hash.match(/^#comment-(\d+)$/);
    if (!match) return;

    const target = document.getElementById(`comment-${match[1]}`);
    if (!target) return;

    window.requestAnimationFrame(() => {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        target.classList.add('ring-2', 'ring-rose-300', 'ring-offset-2');
        window.setTimeout(() => {
            target.classList.remove('ring-2', 'ring-rose-300', 'ring-offset-2');
        }, 2200);
    });
}


/* =================================================================
   SMOOTH COMMENTS COLLAPSE
   Plynulé otevření / zavření + plynulá změna výšky po AJAXu.
================================================================= */

const COMMENTS_ANIMATION_MS = 240;

function setCommentsHostOpen(host, open, options = {}) {
    if (!host || host.dataset.commentsAnimating === '1') return;

    const focusPostId = options.focusPostId || null;

    host.dataset.commentsAnimating = '1';
    host.style.overflow = 'hidden';
    host.style.transition =
        `height ${COMMENTS_ANIMATION_MS}ms ease, opacity ${COMMENTS_ANIMATION_MS - 40}ms ease`;

    if (open) {
        host.classList.remove('hidden');
        host.dataset.commentsCollapsed = '0';

        host.style.height = '0px';
        host.style.opacity = '0';

        // Force layout so browser sees the 0 -> scrollHeight transition.
        host.getBoundingClientRect();

        const targetHeight = host.scrollHeight;

        window.requestAnimationFrame(() => {
            host.style.height = `${targetHeight}px`;
            host.style.opacity = '1';
        });

        window.setTimeout(() => {
            host.style.height = 'auto';
            host.style.opacity = '';
            host.style.overflow = '';
            host.style.transition = '';
            delete host.dataset.commentsAnimating;

            if (focusPostId) {
                const input = host.querySelector(
                    `[data-comment-composer="${focusPostId}"]`
                );

                window.requestAnimationFrame(() => {
                    if (!input) return;

                    input.dispatchEvent(new Event('input', { bubbles: true }));
                    input.focus();
                });
            }
        }, COMMENTS_ANIMATION_MS + 30);

        return;
    }

    host.dataset.commentsCollapsed = '1';

    const currentHeight = host.getBoundingClientRect().height;

    host.style.height = `${currentHeight}px`;
    host.style.opacity = '1';

    host.getBoundingClientRect();

    window.requestAnimationFrame(() => {
        host.style.height = '0px';
        host.style.opacity = '0';
    });

    window.setTimeout(() => {
        host.classList.add('hidden');
        host.style.height = '';
        host.style.opacity = '';
        host.style.overflow = '';
        host.style.transition = '';
        delete host.dataset.commentsAnimating;
    }, COMMENTS_ANIMATION_MS + 30);
}

function animateCommentsHostResize(host, previousHeight = null) {
    if (!host || host.classList.contains('hidden')) return;

    const startHeight = Number.isFinite(previousHeight)
        ? previousHeight
        : host.getBoundingClientRect().height;

    host.style.overflow = 'hidden';
    host.style.transition = 'none';
    host.style.height = `${startHeight}px`;

    // Po změně innerHTML změříme cílovou výšku obsahu.
    const targetHeight = host.scrollHeight;

    if (Math.abs(targetHeight - startHeight) < 2) {
        host.style.height = 'auto';
        host.style.overflow = '';
        host.style.transition = '';
        return;
    }

    host.getBoundingClientRect();

    host.style.transition = `height ${COMMENTS_ANIMATION_MS}ms ease`;

    window.requestAnimationFrame(() => {
        host.style.height = `${targetHeight}px`;
    });

    window.setTimeout(() => {
        host.style.height = 'auto';
        host.style.overflow = '';
        host.style.transition = '';
    }, COMMENTS_ANIMATION_MS + 30);
}


function initLazyPostComments(root = document) {
    bindCommentsNajaSuccess();

    root.querySelectorAll('[data-comments-toggle]').forEach(toggle => {
        if (toggle.dataset.commentsInitialized === '1') return;
        toggle.dataset.commentsInitialized = '1';

        toggle.addEventListener('click', event => {
            const postId = toggle.dataset.commentsToggle;
            if (!postId) return;

            if (toggle.dataset.commentsEnabled === '0') {
                event.preventDefault();
                event.stopImmediatePropagation();
                return;
            }

            const host = document.querySelector(`[data-comments-host="${postId}"]`);
            if (!host) return;

            if (host.dataset.loaded === '1') {
                event.preventDefault();
                event.stopImmediatePropagation();

                const willOpen = host.classList.contains('hidden')
                    || host.dataset.commentsCollapsed === '1';

                setCommentsHostOpen(host, willOpen, {
                    focusPostId: willOpen ? postId : null,
                });
            }
        }, true);
    });
}

function appendStoredComment(payload) {
    const postId = String(payload.commentAppendPostId);
    const commentId = String(payload.commentAppendId || '');
    let inserted = false;
    document.querySelectorAll(`[data-comments-host="${postId}"]`).forEach(host => {
        const previousHeight = host.getBoundingClientRect().height;
        const wasOpen = !host.classList.contains('hidden') && host.dataset.commentsCollapsed !== '1';
        let list = host.querySelector('[data-comments-list]');
        if (!list) {
            list = document.createElement('div');
            list.className = 'space-y-5 px-3 py-3 sm:px-4';
            list.dataset.commentsList = '';
            host.prepend(list);
        }
        if (commentId && host.querySelector(`[data-comment-id="${commentId}"]`)) return;
        list.insertAdjacentHTML('beforeend', payload.commentAppendHtml);
        inserted = true;
        host.querySelector('[data-comments-empty]')?.remove();
        host.dataset.loaded = '1';
        initCommentReplyUI(host);
        initDiscussionTextareas(host);
        initHeartReactions(host);
        if (wasOpen) animateCommentsHostResize(host, previousHeight);
        else setCommentsHostOpen(host, true);
    });
    if (inserted) document.querySelectorAll(`[data-post-comment-count="${postId}"]`).forEach(node => {
        const count = Number.parseInt(node.textContent || '0', 10) || 0;
        node.textContent = String(Math.max(0, count + (Number(payload.commentCountDelta) || 0)));
    });
    delete window.__clasicoDiscussionScroll?.[postId];
}

function bindCommentsNajaSuccess() {
    if (window.__clasicoCommentsNajaBound) return;
    if (!window.naja) return;

    window.__clasicoCommentsNajaBound = true;

    window.naja.addEventListener('success', event => {
        const payload = event.detail?.payload;
        if (!payload) return;

        if (payload.commentAppendPostId && typeof payload.commentAppendHtml === 'string') {
            appendStoredComment(payload);
        }

        if (payload.commentsPostId && typeof payload.commentsHtml === 'string') {
            const postId = String(payload.commentsPostId);
            const host = document.querySelector(`[data-comments-host="${postId}"]`);

            if (host) {
                const wasLoaded = host.dataset.loaded === '1';
                const wasOpen = !host.classList.contains('hidden')
                    && host.dataset.commentsCollapsed !== '1';

                const previousHeight = wasOpen
                    ? host.getBoundingClientRect().height
                    : 0;

                host.innerHTML = payload.commentsHtml;
                host.dataset.loaded = '1';

                initCommentReplyUI(host);
                scrollToNotificationComment(postId);
                initDiscussionTextareas(host);
                initHeartReactions(host);

                /*
                 * První AJAX načtení plynule otevřeme.
                 * Pokud už byl blok otevřený a AJAX změnil jeho obsah
                 * (nový komentář / archivace / reakce), plynule
                 * animujeme změnu jeho výšky.
                 */
                if (!wasLoaded || !wasOpen) {
                    setCommentsHostOpen(host, true);
                } else {
                    animateCommentsHostResize(host, previousHeight);
                }

                const savedScroll = window.__clasicoDiscussionScroll?.[postId];

                if (Number.isFinite(savedScroll)) {
                    window.requestAnimationFrame(() => {
                        window.scrollTo(0, savedScroll);
                        delete window.__clasicoDiscussionScroll[postId];
                    });
                }
            }

            const countNode = document.querySelector(`[data-post-comment-count="${postId}"]`);
            if (countNode && Number.isFinite(Number(payload.commentCount))) {
                countNode.textContent = String(payload.commentCount);
            }
        }

        if (payload.reaction) {
            updateHeartReaction(payload.reaction);
        }

        if (payload.reactionError) {
            showReactionTooltip(
                window.__clasicoLastHeartTrigger,
                payload.reactionError,
            );
        }

        if (payload.reactionError) {
            console.warn('Reaction rejected:', payload.reactionError);
        }

        if (payload.pollVote) {
            updatePollVoteState(payload.pollVote);
        }

        if (payload.pollVoteError) {
            showReactionTooltip(
                window.__clasicoLastPollTrigger,
                payload.pollVoteError,
            );
        }

        if (payload.postCreated === true) {
            /*
             * /feed vrací pouze HTML právě vytvořeného postu. Homepage preview
             * dál může používat klasický snippet redraw. Helper je idempotentní,
             * takže se bezpečně snese i s inline fallbackem v Latte.
             */
            insertCreatedPostFromPayload(payload);
            hideCreatePostError();
            // Reset a zavření vyřizuje konkrétní odeslaný formulář v Promise.
            // Globální success event nesmí smazat jiný ani novější koncept.
        }

        if (payload.postCreateRejected === true) {
            /*
             * Při validační / social embed chybě modal zůstává otevřený a
             * hlavně se nepřekreslí jeho DOM. Uživatel tedy nepřijde o text
             * ani URL a může hodnotu rovnou opravit nebo modal normálně zavřít.
             */
            const form = document.querySelector('[data-create-post-form]');
            const typeInput = form?.querySelector('input[name="type"], input[name$="[type]"]');
            const message = payload.flash?.message || 'Příspěvek se nepodařilo uložit.';

            // Chyba je zároveň vykreslená server-side v redrawnutém
            // newPostForm snippetu. Toto ponecháváme jako klientský fallback.
            showCreatePostError(message);

            window.requestAnimationFrame(() => {
                // Po snippet redraw znovu srovnáme composer podle hodnoty hidden
                // inputu `type`, aby Social/Foto/Anketa panel nezůstal skrytý.
                initCreatePostComposer(document);
                const scroll = form?.querySelector('[data-create-post-scroll]');
                if (scroll) scroll.scrollTop = 0;

                if (typeInput?.value === 'social') {
                    form?.querySelector('input[name="media_url"], input[name$="[media_url]"]')?.focus();
                }
            });
        }

        if (payload.postNotificationPreference) {
            applyPostNotificationPreference(payload.postNotificationPreference);
            closeOpenModerationDropdowns();
        }

        if (payload.moderation) {
            applyModerationPayload(payload.moderation);
            closeOpenModerationDropdowns();
        }

        if (payload.moderationError) {
            closeOpenModerationDropdowns();
        }

        if (payload.flash?.message) {
            showAjaxFlash(
                payload.flash.message,
                payload.flash.type || 'info',
            );
        }

        /*
         * Flash komponenty znovu inicializuje globální Naja `complete`
         * callback přes initAllPageComponents(). Tady se dříve volala
         * neimportovaná initUniversalFlashMessages(), což po úspěšném AJAXu
         * vyhazovalo ReferenceError.
         */
    });
}


function initDiscussionTextareas(root = document) {
    root.querySelectorAll('[data-discussion-form] textarea').forEach(textarea => {
        if (textarea.dataset.discussionTextareaInitialized === '1') return;
        textarea.dataset.discussionTextareaInitialized = '1';

        const resize = () => {
            textarea.style.height = 'auto';

            if (textarea.scrollHeight > 0) {
                textarea.style.height = `${textarea.scrollHeight}px`;
            } else {
                textarea.style.removeProperty('height');
                return;
            }

            const styles = getComputedStyle(textarea);
            const lineHeight = parseFloat(styles.lineHeight) || 20;
            const paddingTop = parseFloat(styles.paddingTop) || 0;
            const paddingBottom = parseFloat(styles.paddingBottom) || 0;

            const singleLineHeight = lineHeight + paddingTop + paddingBottom;
            const multiline = textarea.scrollHeight > singleLineHeight + 2;

            const row = textarea.closest('[data-comment-row]');
            const box = textarea.closest('[data-comment-box]');

            row?.classList.toggle('items-center', !multiline);
            row?.classList.toggle('items-end', multiline);

            box?.classList.toggle('items-center', !multiline);
            box?.classList.toggle('items-end', multiline);
        };

        textarea.addEventListener('input', resize);

        /*
         * Důležité pro textarea, která byla inicializovaná
         * ve skrytém comments hostu.
         */
        textarea.addEventListener('focus', () => {
            window.requestAnimationFrame(resize);
        });

        textarea.addEventListener('keydown', event => {
            if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
                event.preventDefault();
                textarea.form?.requestSubmit();
            }
        });

        resize();
    });
}


function initHeartReactions(root = document) {
    if (window.__clasicoHeartHandlerInitialized) return;

    window.__clasicoHeartHandlerInitialized = true;

    document.addEventListener('click', event => {
        const element = event.target.closest(
            '[data-heart-target][data-heart-id]'
        );

        if (!element) return;

        const url = element.getAttribute('href');

        if (!url || !window.naja) {
            event.preventDefault();
            return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();

        if (element.dataset.heartLoading === '1') return;

        window.__clasicoLastHeartTrigger = element;

        element.dataset.heartLoading = '1';
        element.classList.add('pointer-events-none', 'opacity-60');

        window.naja.makeRequest('GET', url, null, {
            history: false,
        }).finally(() => {
            delete element.dataset.heartLoading;
            element.classList.remove('pointer-events-none', 'opacity-60');
        });
    }, true);
}

function updateHeartReaction(reaction) {
    const target = String(reaction.target || '');
    const id = String(reaction.id || '');

    if (!target || !id) return;

    const element = document.querySelector(
        `[data-heart-target="${target}"][data-heart-id="${id}"]`
    );

    if (!element) return;

    const count = element.querySelector('[data-heart-count]');
    const icon = element.querySelector('i');

    if (count) {
        const reactionCount = Number(reaction.count ?? 0);

        count.textContent = String(reactionCount);
        count.classList.toggle('hidden', reactionCount < 1);
    }

    if (icon) {
        icon.classList.toggle('fa-regular', !reaction.active);
        icon.classList.toggle('fa-solid', !!reaction.active);
        icon.classList.toggle('text-rose-500', !!reaction.active);
    }

    element.classList.toggle('text-rose-600', !!reaction.active);
}


/* =================================================================
   DISCUSSION FORM AJAX
   Všechny formy jsou zachycené delegovaně, takže fungují i po innerHTML.
================================================================= */
function initDiscussionFormsAjax(root = document) {
    if (window.__clasicoDiscussionSubmitInitialized) return;

    window.__clasicoDiscussionSubmitInitialized = true;
    window.__clasicoDiscussionScroll = window.__clasicoDiscussionScroll || {};

    document.addEventListener('submit', event => {
        const form = event.target.closest('form[data-discussion-form]');

        if (!form || !window.naja) return; // Bez JS transportu ponech standardní POST/redirect.

        event.preventDefault();
        event.stopImmediatePropagation();

        if (form.dataset.discussionSubmitting === '1') {
            return;
        }

        if (!form.checkValidity()) {
            form.reportValidity();
            return;
        }

        // Capture handler běží před dalšími submit listenery; synchronizaci ankety
        // proto vyvolej přímo ještě před sestavením odesílaných dat.
        form.__clasicoPreparePost?.();
        const draft = captureDiscussionDraft(form);
        const submitter = event.submitter || form.querySelector('button[type="submit"][name], input[type="submit"][name]');
        const formData = new FormData(form);

        // FormData(form) standardně nemusí obsahovat kliknutý submit button.
        // Nette ho ale potřebuje k vyvolání onSuccess daného formuláře.
        if (submitter?.name && !formData.has(submitter.name)) {
            formData.append(submitter.name, submitter.value || '');
        }

        const postComposer = form.querySelector('[data-comment-composer]');
        const postId = postComposer?.dataset.commentComposer;

        if (postId) {
            window.__clasicoDiscussionScroll[postId] = window.scrollY;
        }

        form.querySelector('[data-discussion-submit-error]')?.remove();
        form.dataset.discussionSubmitting = '1';

        form.querySelectorAll('button[type="submit"], input[type="submit"]').forEach(button => {
            button.disabled = true;
            button.classList.add('opacity-60');
        });

        const method = (form.method || 'POST').toUpperCase();
        const url = form.action || window.location.href;

        Promise.resolve().then(() => window.naja.makeRequest(method, url, formData, {
            history: false,
        })).then(payload => {
            if (clearStoredDiscussionDraft(form, draft, payload) && form.matches('[data-create-post-form]')) {
                resetCreatePostComposer();
                window.__clasicoCloseCreatePostModal?.();
            }
        }).catch(error => {
            console.error('Discussion save failed', error);
            let message = form.querySelector('[data-discussion-submit-error]');
            if (!message) {
                message = document.createElement('p');
                message.dataset.discussionSubmitError = '1';
                message.className = 'text-xs text-red-600';
                message.setAttribute('role', 'alert');
                form.append(message);
            }
            message.textContent = 'Odeslání se nepodařilo dokončit. Text zůstal zachovaný; ověřte komentáře před dalším odesláním.';
        }).finally(() => {
            delete form.dataset.discussionSubmitting;

            form.querySelectorAll('button[type="submit"], input[type="submit"]').forEach(button => {
                button.disabled = false;
                button.classList.remove('opacity-60');
            });
        });
    }, true);
}


/* =================================================================
   CREATE POST COMPOSER
   Diskuze / Social / Foto / Anketa
================================================================= */
function showCreatePostError(message) {
    const box = document.querySelector('[data-create-post-error]');
    const text = box?.querySelector('[data-create-post-error-message]');

    if (!box || !text) return;

    text.textContent = String(message || 'Příspěvek se nepodařilo uložit.');
    box.classList.remove('hidden');
}

function hideCreatePostError() {
    const box = document.querySelector('[data-create-post-error]');
    const text = box?.querySelector('[data-create-post-error-message]');

    if (text) text.textContent = '';
    box?.classList.add('hidden');
}

function styleCreatePostTypeButton(button, active) {
    if (!button) return;

    button.dataset.createPostActive = active ? '1' : '0';
    button.classList.toggle('ui-secondary', active);
    button.classList.toggle('border-transparent', !active);
    button.classList.toggle('bg-transparent', !active);
    button.classList.toggle('text-slate-500', !active);
}

function normalizeCreatePostTag(raw) {
    return String(raw || '')
        .trim()
        .replace(/^#+/u, '')
        .replace(/[#,;]+$/u, '')
        .replace(/\s+/gu, '')
        .slice(0, 80);
}

function getCreatePostTagState() {
    const form = document.querySelector('[data-create-post-form]');
    if (!form) return null;

    const hidden = form.querySelector('[data-tag-value]');
    const host = form.querySelector('[data-tag-selected]');
    const entry = form.querySelector('[data-tag-entry]');

    if (!hidden || !host) return null;

    return { form, hidden, host, entry };
}

function readCreatePostTags(hidden) {
    const result = [];
    const seen = new Set();

    String(hidden?.value || '')
        .split(/[\s,;]+/u)
        .map(normalizeCreatePostTag)
        .filter(Boolean)
        .forEach(tag => {
            const key = tag.toLocaleLowerCase('cs-CZ');
            if (seen.has(key)) return;
            seen.add(key);
            result.push(tag);
        });

    return result.slice(0, 5);
}

function writeCreatePostTags(tags) {
    const state = getCreatePostTagState();
    if (!state) return;

    const clean = [];
    const seen = new Set();

    tags.map(normalizeCreatePostTag).filter(Boolean).forEach(tag => {
        const key = tag.toLocaleLowerCase('cs-CZ');
        if (seen.has(key) || clean.length >= 5) return;
        seen.add(key);
        clean.push(tag);
    });

    state.hidden.value = clean.map(tag => `#${tag}`).join(' ');
    renderCreatePostTags();
}

function renderCreatePostTags() {
    const state = getCreatePostTagState();
    if (!state) return;

    const tags = readCreatePostTags(state.hidden);
    state.host.replaceChildren();

    if (tags.length === 0) {
        const empty = document.createElement('span');
        empty.className = 'text-[10px] text-slate-600';
        empty.textContent = 'Zatím žádný tag';
        state.host.appendChild(empty);
    } else {
        tags.forEach(tag => {
            const chip = document.createElement('span');
            chip.className = 'inline-flex h-7 items-center gap-1 rounded-full border pl-2.5 pr-1 text-[11px] font-semibold ui-secondary';

            const label = document.createElement('span');
            label.textContent = `#${tag}`;

            const remove = document.createElement('button');
            remove.type = 'button';
            remove.dataset.tagRemove = tag;
            remove.className = 'grid h-5 w-5 place-items-center rounded-full opacity-60 transition hover:bg-white/10 hover:opacity-100';
            remove.setAttribute('aria-label', `Odebrat #${tag}`);
            remove.innerHTML = '<i class="fa-solid fa-xmark text-[9px]"></i>';

            chip.append(label, remove);
            state.host.appendChild(chip);
        });
    }

    const activeKeys = new Set(tags.map(tag => tag.toLocaleLowerCase('cs-CZ')));
    state.form.querySelectorAll('[data-tag-preset]').forEach(button => {
        const key = normalizeCreatePostTag(button.dataset.tagPreset).toLocaleLowerCase('cs-CZ');
        const active = activeKeys.has(key);
        button.classList.toggle('ui-secondary', active);
        button.classList.toggle('opacity-60', active);
    });
}

function addCreatePostTags(raw) {
    const state = getCreatePostTagState();
    if (!state) return;

    const current = readCreatePostTags(state.hidden);
    const additions = String(raw || '')
        .split(/[\s,;]+/u)
        .map(normalizeCreatePostTag)
        .filter(Boolean);

    writeCreatePostTags([...current, ...additions]);
}

function removeCreatePostTag(tag) {
    const state = getCreatePostTagState();
    if (!state) return;

    const removeKey = normalizeCreatePostTag(tag).toLocaleLowerCase('cs-CZ');
    writeCreatePostTags(
        readCreatePostTags(state.hidden).filter(
            item => item.toLocaleLowerCase('cs-CZ') !== removeKey
        )
    );
}

function resetCreatePostComposer() {
    const form = document.querySelector('[data-create-post-form]');
    if (!form) return;

    hideCreatePostError();
    form.reset();

    const setValue = (selector, value) => {
        const input = form.querySelector(selector);
        if (input) input.value = value;
    };

    setValue('input[name="type"], input[name$="[type]"]', 'text');
    setValue('input[name="media_url"], input[name$="[media_url]"]', '');
    setValue('input[name="source_name"], input[name$="[source_name]"]', '');
    setValue('input[name="source_url"], input[name$="[source_url]"]', '');
    setValue('input[name="tags"], input[name$="[tags]"]', '');
    setValue('input[name="poll_options_json"], input[name$="[poll_options_json]"]', '[]');

    const text = form.querySelector('[data-create-post-text]');
    if (text) {
        text.value = '';
        text.placeholder = 'Co se děje?';
        text.style.removeProperty('height');
        text.dispatchEvent(new Event('input', { bubbles: true }));
    }

    const sourceType = form.querySelector('select[name="source_type"], select[name$="[source_type]"]');
    if (sourceType) sourceType.value = '';

    const advanced = form.querySelector('[data-create-post-advanced]');
    if (advanced) advanced.open = false;

    renderCreatePostTags();

    const pollOptions = form.querySelector('[data-poll-options]');
    if (pollOptions) {
        const rows = Array.from(pollOptions.querySelectorAll('[data-poll-option-row]'));
        rows.slice(2).forEach(row => row.remove());
        pollOptions.querySelectorAll('[data-poll-option]').forEach((input, index) => {
            input.value = '';
            input.placeholder = `Možnost ${index + 1}`;
        });
        pollOptions.querySelectorAll('[data-poll-option-number]').forEach((node, index) => {
            node.textContent = String(index + 1);
        });
        pollOptions.querySelectorAll('[data-poll-option-remove]').forEach(button => {
            button.classList.add('invisible');
        });
    }

    const commentsEnabled = form.querySelector('input[name="comments_enabled"], input[name$="[comments_enabled]"]');
    if (commentsEnabled) commentsEnabled.checked = true;

    const allowVoteChange = form.querySelector('input[name="poll_allow_vote_change"], input[name$="[poll_allow_vote_change]"]');
    if (allowVoteChange) allowVoteChange.checked = true;

    const multipleChoices = form.querySelector('input[name="poll_multiple_choices"], input[name$="[poll_multiple_choices]"]');
    if (multipleChoices) multipleChoices.checked = false;

    form.querySelector('[data-create-post-fields="media"]')?.classList.add('hidden');
    form.querySelector('[data-create-post-fields="poll"]')?.classList.add('hidden');

    document.querySelectorAll('[data-create-post-type]').forEach(button => {
        styleCreatePostTypeButton(button, button.dataset.createPostType === 'text');
    });

    const title = document.querySelector('[data-create-post-title]');
    const subtitle = document.querySelector('[data-create-post-subtitle]');
    const textLabel = form.querySelector('[data-post-text-label]');

    if (title) title.textContent = 'Nový příspěvek';
    if (subtitle) subtitle.textContent = 'Diskuze';
    if (textLabel) textLabel.textContent = 'Text příspěvku';
}

function initCreatePostComposer(root = document) {
    const form = document.querySelector('[data-create-post-form]');

    if (!form) return;

    const typeInput = form.querySelector('input[name="type"], input[name$="[type]"]');
    const mediaPanel = form.querySelector('[data-create-post-fields="media"]');
    const pollPanel = form.querySelector('[data-create-post-fields="poll"]');
    const mediaLabel = form.querySelector('[data-media-url-label]');
    const mediaIcon = form.querySelector('[data-media-url-icon]');
    const mediaInput = form.querySelector('input[name="media_url"], input[name$="[media_url]"]');
    const textInput = form.querySelector('[data-create-post-text]');
    const textLabel = form.querySelector('[data-post-text-label]');
    const title = document.querySelector('[data-create-post-title]');
    const subtitle = document.querySelector('[data-create-post-subtitle]');
    const pollOptionsHost = form.querySelector('[data-poll-options]');
    const pollOptionsJson = form.querySelector('input[name="poll_options_json"], input[name$="[poll_options_json]"]');

    const config = {
        text: {
            title: 'Nový příspěvek',
            subtitle: 'Diskuze',
            textLabel: 'Text příspěvku',
            placeholder: 'Co se děje?',
        },
        social: {
            title: 'Přidat ze sociální sítě',
            subtitle: 'Social',
            textLabel: 'Komentář k příspěvku',
            placeholder: 'Napište něco k odkazu…',
        },
        photo: {
            title: 'Přidat foto',
            subtitle: 'Foto',
            textLabel: 'Komentář k fotce',
            placeholder: 'Napište něco k fotce…',
        },
        poll: {
            title: 'Nová anketa',
            subtitle: 'Anketa',
            textLabel: 'Otázka / popis ankety',
            placeholder: 'Na co se chcete zeptat?',
        },
    };

    const syncPollOptions = () => {
        if (!pollOptionsHost || !pollOptionsJson) return;

        const values = Array.from(
            pollOptionsHost.querySelectorAll('[data-poll-option]')
        ).map(input => input.value);

        pollOptionsJson.value = JSON.stringify(values);
    };

    const renumberPollOptions = () => {
        if (!pollOptionsHost) return;

        const rows = Array.from(
            pollOptionsHost.querySelectorAll('[data-poll-option-row]')
        );

        rows.forEach((row, index) => {
            const number = row.querySelector('[data-poll-option-number]');
            const input = row.querySelector('[data-poll-option]');
            const remove = row.querySelector('[data-poll-option-remove]');

            if (number) number.textContent = String(index + 1);

            if (input) {
                input.placeholder = `Možnost ${index + 1}`;
            }

            if (remove) {
                remove.classList.toggle('invisible', rows.length <= 2);
            }
        });

        syncPollOptions();
    };

    const createPollOptionRow = () => {
        const count = pollOptionsHost?.querySelectorAll('[data-poll-option-row]').length ?? 0;

        const row = document.createElement('div');
        row.className = 'flex items-center gap-1.5';
        row.dataset.pollOptionRow = '1';

        row.innerHTML = `
            <span class="w-5 text-center text-[10px] font-bold text-slate-400"
                  data-poll-option-number>${count + 1}</span>

            <input
                type="text"
                data-poll-option
                maxlength="500"
                class="h-10 min-w-0 flex-1 rounded-md px-3 text-base sm:text-sm ui-control"
                placeholder="Možnost ${count + 1}"
            >

            <button
                type="button"
                data-poll-option-remove
                class="grid h-8 w-8 place-items-center rounded-lg text-slate-600 transition hover:bg-rose-500/10 hover:text-rose-300"
                aria-label="Odstranit možnost"
            >
                <i class="fa-duotone fa-solid fa-xmark"></i>
            </button>
        `;

        return row;
    };

    const setType = requestedType => {
        const type = config[requestedType] ? requestedType : 'text';

        const typeButton = document.querySelector(
            `[data-create-post-type="${type}"]`
        );

        if (typeButton?.dataset.createPostTypeDisabled === '1') {
            showReactionTooltip(
                typeButton,
                'Tento typ příspěvku teď nemáte povolený.',
            );
            return;
        }

        const item = config[type];

        if (typeInput) {
            typeInput.value = type;
            typeInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        mediaPanel?.classList.toggle(
            'hidden',
            type !== 'social' && type !== 'photo'
        );

        pollPanel?.classList.toggle('hidden', type !== 'poll');

        document.querySelectorAll('[data-create-post-type]').forEach(button => {
            styleCreatePostTypeButton(button, button.dataset.createPostType === type);
        });

        if (title) title.textContent = item.title;
        if (subtitle) subtitle.textContent = item.subtitle;
        if (textLabel) textLabel.textContent = item.textLabel;
        if (textInput) textInput.placeholder = item.placeholder;

        if (type === 'social') {
            if (mediaLabel) mediaLabel.textContent = 'Odkaz na sociální příspěvek';
            if (mediaInput) mediaInput.placeholder = 'YouTube, Instagram, TikTok, X, Facebook…';
            mediaIcon?.classList.remove('fa-image');
            mediaIcon?.classList.remove('fa-video');
            mediaIcon?.classList.add('fa-share-nodes');
        }

        if (type === 'photo') {
            if (mediaLabel) mediaLabel.textContent = 'URL obrázku';
            if (mediaInput) mediaInput.placeholder = 'https://.../foto.jpg';
            mediaIcon?.classList.remove('fa-video');
            mediaIcon?.classList.remove('fa-share-nodes');
            mediaIcon?.classList.add('fa-image');
        }

        if (type === 'poll') {
            renumberPollOptions();

            window.requestAnimationFrame(() => {
                pollOptionsHost?.querySelector('[data-poll-option]')?.focus();
            });
        } else {
            window.requestAnimationFrame(() => textInput?.focus());
        }
    };

    if (!window.__clasicoCreatePostDelegationBound) {
        window.__clasicoCreatePostDelegationBound = true;

        document.addEventListener('click', event => {
            const typeButton = event.target.closest('[data-create-post-type]');

            if (typeButton) {
                window.__clasicoSetCreatePostType?.(
                    typeButton.dataset.createPostType || 'text'
                );
                return;
            }

            const tagPreset = event.target.closest('[data-tag-preset]');
            if (tagPreset) {
                event.preventDefault();
                addCreatePostTags(tagPreset.dataset.tagPreset || '');
                return;
            }

            const tagRemove = event.target.closest('[data-tag-remove]');
            if (tagRemove) {
                event.preventDefault();
                removeCreatePostTag(tagRemove.dataset.tagRemove || '');
                return;
            }

            const addButton = event.target.closest('[data-poll-option-add]');

            if (addButton && pollOptionsHost) {
                const rows = pollOptionsHost.querySelectorAll('[data-poll-option-row]');

                if (rows.length >= 10) return;

                const row = createPollOptionRow();
                pollOptionsHost.appendChild(row);
                renumberPollOptions();
                row.querySelector('[data-poll-option]')?.focus();
                return;
            }

            const removeButton = event.target.closest('[data-poll-option-remove]');

            if (removeButton && pollOptionsHost) {
                const rows = pollOptionsHost.querySelectorAll('[data-poll-option-row]');

                if (rows.length <= 2) return;

                removeButton.closest('[data-poll-option-row]')?.remove();
                renumberPollOptions();
            }
        });

        document.addEventListener('keydown', event => {
            const entry = event.target.closest?.('[data-tag-entry]');
            if (!entry) return;

            if (event.key === 'Enter' || event.key === ' ' || event.key === ',') {
                const value = entry.value.trim();
                if (!value) return;
                event.preventDefault();
                addCreatePostTags(value);
                entry.value = '';
                return;
            }

            if (event.key === 'Backspace' && entry.value === '') {
                const state = getCreatePostTagState();
                const tags = state ? readCreatePostTags(state.hidden) : [];
                if (tags.length) removeCreatePostTag(tags[tags.length - 1]);
            }
        });

        document.addEventListener('focusout', event => {
            const entry = event.target.closest?.('[data-tag-entry]');
            if (!entry || !entry.value.trim()) return;
            addCreatePostTags(entry.value);
            entry.value = '';
        });

        document.addEventListener('input', event => {
            if (event.target.matches('[data-poll-option]')) {
                syncPollOptions();
            }

            if (event.target.matches('input[name="media_url"], input[name$="[media_url]"]')) {
                hideCreatePostError();
            }
        });

        document.addEventListener('submit', event => {
            const currentForm = event.target.closest('[data-create-post-form]');

            if (!currentForm) return;

            // U ankety musí být JSON synchronizovaný těsně před FormData().
            syncPollOptions();

            // Pojistka: hidden type musí vždy obsahovat aktuálně zvolený režim.
            const activeButton = document.querySelector(
                '[data-create-post-type][data-create-post-active="1"]'
            );

            if (typeInput && activeButton?.dataset.createPostType) {
                typeInput.value = activeButton.dataset.createPostType;
            }
        }, true);
    }

    // Po každém Naja redraw se init spustí znovu. Globální delegace tedy
    // vždy volá nejnovější setType() navázaný na právě živý formulář.
    window.__clasicoSetCreatePostType = setType;

    form.__clasicoPreparePost = () => {
        syncPollOptions();
        const activeButton = document.querySelector('[data-create-post-type][data-create-post-active="1"]');
        if (typeInput && activeButton?.dataset.createPostType) typeInput.value = activeButton.dataset.createPostType;
    };

    const currentType = typeInput?.value || 'text';
    setType(currentType);
    renumberPollOptions();
    renderCreatePostTags();

    const advanced = form.querySelector('[data-create-post-advanced]');
    const hasAdvancedValue = [
        form.querySelector('input[name="source_name"], input[name$="[source_name]"]')?.value,
        form.querySelector('input[name="source_url"], input[name$="[source_url]"]')?.value,
        form.querySelector('select[name="source_type"], select[name$="[source_type]"]')?.value,
    ].some(value => String(value || '').trim() !== '');
    if (advanced && hasAdvancedValue) advanced.open = true;
}


function showReactionTooltip(trigger, message) {
    if (!trigger || !message) return;

    document.querySelector('[data-reaction-tooltip]')?.remove();

    const tooltipEl = document.createElement('div');
    tooltipEl.setAttribute('role', 'tooltip');
    tooltipEl.setAttribute('data-reaction-tooltip', '1');
    tooltipEl.className =
        'absolute z-[120] invisible inline-block max-w-[260px] ' +
        'rounded-md bg-slate-900 px-2.5 py-1.5 text-[11px] font-semibold ' +
        'leading-4 text-white opacity-0 shadow-lg';

    tooltipEl.innerHTML = `
        <span></span>
        <div class="tooltip-arrow" data-popper-arrow></div>
    `;

    tooltipEl.querySelector('span').textContent = message;
    document.body.appendChild(tooltipEl);

    const tooltip = new Tooltip(
        tooltipEl,
        trigger,
        {
            placement: 'top',
            triggerType: 'none',
        }
    );

    tooltip.show();

    window.setTimeout(() => {
        tooltip.hide();
        window.setTimeout(() => tooltipEl.remove(), 200);
    }, 2400);
}


/* =================================================================
   POLL COUNTDOWN
================================================================= */
function initPollCountdowns(root = document) {
    root.querySelectorAll('[data-poll][data-poll-ends-at]').forEach(poll => {
        updatePollDeadlineLabel(poll);
        updatePollVoteChangeInfo(
            poll,
            poll.dataset.pollAllowChange === '1',
        );
        updatePollCountdown(poll);
    });

    if (window.__clasicoPollCountdownTimer) return;

    window.__clasicoPollCountdownTimer = window.setInterval(() => {
        document.querySelectorAll('[data-poll][data-poll-ends-at]').forEach(poll => {
            updatePollCountdown(poll);
        });
    }, 1000);
}

function updatePollDeadlineLabel(poll) {
    const endsAt = poll.dataset.pollEndsAt;
    if (!endsAt) return;

    const endMs = Date.parse(endsAt);
    if (!Number.isFinite(endMs)) return;

    const deadline = poll.querySelector('[data-poll-deadline]');
    if (!deadline) return;

    const formatted = new Intl.DateTimeFormat('cs-CZ', {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(new Date(endMs));

    deadline.textContent = `do ${formatted}`;
}

function updatePollVoteChangeInfo(poll, allowChange) {
    const info = poll.querySelector('[data-poll-vote-change-info]');
    if (!info) return;

    const icon = info.querySelector('i');
    const label = info.querySelector('span');

    if (label) {
        label.textContent = allowChange
            ? 'Hlas lze změnit'
            : 'Hlas je konečný';
    }

    if (icon) {
        icon.className = allowChange
            ? 'fa-duotone fa-solid fa-rotate'
            : 'fa-duotone fa-solid fa-lock';
    }
}

function updatePollCountdown(poll) {
    const endsAt = poll.dataset.pollEndsAt;
    if (!endsAt) return;

    const endMs = Date.parse(endsAt);
    if (!Number.isFinite(endMs)) return;

    const remaining = endMs - Date.now();
    const node = poll.querySelector('[data-poll-countdown]');

    if (remaining <= 0) {
        poll.dataset.pollExpired = '1';

        if (node) {
            node.textContent = 'Hlasování ukončeno';
        }

        poll.querySelectorAll('[data-poll-vote]').forEach(option => {
            option.classList.add('pointer-events-none', 'opacity-50');
            option.setAttribute('aria-disabled', 'true');
        });

        poll.querySelector('[data-poll-submit-selection]')?.classList.add('hidden');
        return;
    }

    poll.dataset.pollExpired = '0';

    const totalSeconds = Math.floor(remaining / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    let text;

    if (days > 0) {
        text = `Zbývá ${days} d ${hours} h ${minutes} min`;
    } else if (hours > 0) {
        text = `Zbývá ${hours} h ${minutes} min ${seconds} s`;
    } else {
        text = `Zbývá ${minutes} min ${seconds} s`;
    }

    if (node) {
        node.textContent = text;
    }
}

/* =================================================================
   POLL VOTING
   Single choice = změna hlasu.
   Multiple choice = toggle jednotlivých možností.
================================================================= */
function initPollVoting(root = document) {
    if (window.__clasicoPollVotingInitialized) return;

    window.__clasicoPollVotingInitialized = true;

    document.addEventListener('click', event => {
        const submit = event.target.closest('[data-poll-submit-selection]');

        if (submit) {
            event.preventDefault();
            event.stopImmediatePropagation();

            const poll = submit.closest('[data-poll]');
            const baseUrl = submit.dataset.pollSubmitUrl;

            if (!poll || !baseUrl || !window.naja) return;
            if (submit.dataset.pollLoading === '1') return;

            const selectedIds = Array.from(
                poll.querySelectorAll('[data-poll-option-id][data-poll-draft-selected="1"]')
            ).map(option => Number(option.dataset.pollOptionId));

            if (selectedIds.length === 0) {
                showReactionTooltip(submit, 'Vyberte alespoň jednu možnost.');
                return;
            }

            const url = new URL(baseUrl, window.location.href);
            url.searchParams.set('optionIds', JSON.stringify(selectedIds));

            window.__clasicoLastPollTrigger = submit;
            submit.dataset.pollLoading = '1';
            submit.disabled = true;
            submit.classList.add('opacity-70');

            window.naja.makeRequest('GET', url.toString(), null, {
                history: false,
            }).then(payload => {
                applyPollVotePayload(payload, submit);
            }).finally(() => {
                delete submit.dataset.pollLoading;
                submit.disabled = false;
                submit.classList.remove('opacity-70');
            });

            return;
        }

        const option = event.target.closest('[data-poll-vote]');

        if (!option) return;

        const poll = option.closest('[data-poll]');
        if (!poll) return;

        const multiple = poll.dataset.pollMultiple === '1';
        const allowChange = poll.dataset.pollAllowChange === '1';

        event.preventDefault();

        if (poll.dataset.pollExpired === '1') {
            event.stopImmediatePropagation();
            showReactionTooltip(option, 'Hlasování už skončilo.');
            return;
        }
        event.stopImmediatePropagation();

        /*
         * Multiple + zakázaná změna:
         * před prvním potvrzením pouze lokálně skládáme výběr.
         */
        if (multiple && !allowChange) {
            const hasPersistedVote = Array.from(
                poll.querySelectorAll('[data-poll-option-id]')
            ).some(item => item.dataset.pollSelected === '1');

            if (hasPersistedVote) {
                showReactionTooltip(option, 'Svůj hlas už v této anketě změnit nemůžete.');
                return;
            }

            const active = option.dataset.pollDraftSelected === '1';
            option.dataset.pollDraftSelected = active ? '0' : '1';

            option.classList.toggle('border-violet-400/35', !active);
            option.classList.toggle('bg-violet-500/[0.08]', !active);
            option.classList.toggle('ui-line', active);
            option.classList.toggle('ui-surface-muted', active);

            const check = option.querySelector('[data-poll-check]');
            const icon = check?.querySelector('i');

            check?.classList.toggle('text-violet-300', !active);
            check?.classList.toggle('text-slate-600', active);

            if (icon) {
                icon.className = `fa-duotone ${!active ? 'fa-solid fa-square-check' : 'fa-regular fa-square'}`;
            }

            const submitButton = poll.querySelector('[data-poll-submit-selection]');
            const anySelected = poll.querySelector('[data-poll-draft-selected="1"]');

            submitButton?.classList.toggle('hidden', !anySelected);
            return;
        }

        const url = option.getAttribute('href');

        if (!url || !window.naja) return;
        if (option.dataset.pollLoading === '1') return;

        window.__clasicoLastPollTrigger = option;
        option.dataset.pollLoading = '1';

        poll.querySelectorAll('[data-poll-vote]').forEach(item => {
            item.classList.add('pointer-events-none', 'opacity-70');
        });

        window.naja.makeRequest('GET', url, null, {
            history: false,
        }).then(payload => {
            applyPollVotePayload(payload, option);
        }).finally(() => {
            delete option.dataset.pollLoading;

            poll.querySelectorAll('[data-poll-vote]').forEach(item => {
                item.classList.remove('pointer-events-none', 'opacity-70');
            });
        });
    }, true);
}

function applyPollVotePayload(payload, trigger = null) {
    if (payload?.pollVote) {
        updatePollVoteState(payload.pollVote);
    }

    if (payload?.pollVoteError) {
        showReactionTooltip(trigger || window.__clasicoLastPollTrigger, payload.pollVoteError);
    }
}

function updatePollVoteState(state) {
    const pollId = String(state.pollId || '');
    const poll = document.querySelector(`[data-poll="${pollId}"]`);

    if (!poll) return;

    const selected = new Set(
        (state.selectedOptionIds || []).map(id => String(id))
    );

    poll.dataset.pollMultiple = state.multipleChoices ? '1' : '0';
    poll.dataset.pollAllowChange = state.allowVoteChange ? '1' : '0';

    poll.querySelectorAll('[data-poll-option-id]').forEach(option => {
        const optionId = String(option.dataset.pollOptionId);
        const isSelected = selected.has(optionId);
        const percent = Number(state.percents?.[optionId] ?? 0);

        option.dataset.pollSelected = isSelected ? '1' : '0';
        option.dataset.pollDraftSelected = isSelected ? '1' : '0';

        option.classList.toggle('border-violet-400/35', isSelected);
        option.classList.toggle('bg-violet-500/[0.08]', isSelected);
        option.classList.toggle('ui-line', !isSelected);
        option.classList.toggle('ui-surface-muted', !isSelected);

        const progress = option.querySelector('[data-poll-progress]');
        const percentNode = option.querySelector('[data-poll-percent]');
        const check = option.querySelector('[data-poll-check]');
        const icon = check?.querySelector('i');

        if (progress) {
            progress.style.width = `${percent}%`;
        }

        if (percentNode) {
            percentNode.textContent = `${percent} %`;
        }

        if (check) {
            check.classList.toggle('text-violet-300', isSelected);
            check.classList.toggle('text-slate-600', !isSelected);
        }

        if (icon) {
            icon.className = state.multipleChoices
                ? `fa-duotone ${isSelected ? 'fa-solid fa-square-check' : 'fa-regular fa-square'}`
                : `fa-duotone ${isSelected ? 'fa-solid fa-circle-check' : 'fa-regular fa-circle'}`;
        }
    });

    const total = poll.querySelector('[data-poll-total]');

    if (total) {
        total.textContent = String(state.totalVotes ?? 0);
    }

    const submitButton = poll.querySelector('[data-poll-submit-selection]');

    if (submitButton) {
        submitButton.classList.add('hidden');
    }
}


/* =================================================================
   MODERATION
   Akce se posílají AJAXem, ale UI stav po změně řeší Nette snippet redraw.
================================================================= */
function initContentModeration(root = document) {
    if (window.__clasicoModerationInitialized) return;
    window.__clasicoModerationInitialized = true;

    document.addEventListener('click', event => {
        const trigger = event.target.closest('[data-moderation-action]');
        if (!trigger) return;

        const url = trigger.getAttribute('href');
        if (!url || !window.naja) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        if (trigger.dataset.moderationLoading === '1') return;

        const destructive = ['archive-post', 'archive-comment'].includes(
            trigger.dataset.moderationAction
        );

        if (destructive) {
            const question = trigger.dataset.confirm || 'Opravdu pokračovat?';
            if (!window.confirm(question)) return;
        }

        trigger.dataset.moderationLoading = '1';
        trigger.classList.add('pointer-events-none', 'opacity-60');

        window.naja.makeRequest('GET', url, null, {
            history: false,
        }).finally(() => {
            delete trigger.dataset.moderationLoading;
            trigger.classList.remove('pointer-events-none', 'opacity-60');
        });
    }, true);
}

function applyPostNotificationPreference(state) {
    if (!state || !state.postId) return;

    const menu = document.querySelector(`[data-post-notification-menu="${String(state.postId)}"]`);
    if (!menu) return;

    const webLabel = menu.querySelector('[data-post-notification-label="web"]');
    const emailLabel = menu.querySelector('[data-post-notification-label="email"]');
    if (webLabel) {
        webLabel.textContent = state.webComments ? 'Vypnout ve zvonečku' : 'Zapnout ve zvonečku';
    }
    if (emailLabel) {
        emailLabel.textContent = state.emailComments ? 'Vypnout e-mailem' : 'Zapnout e-mailem';
    }
}

function applyModerationPayload(state) {
    if (!state || !state.target || !state.id) return;

    const target = String(state.target);
    const id = String(state.id);

    // Archivace / skrytí konkrétního obsahu.
    if (state.removed === true) {
        const node = document.querySelector(
            target === 'post'
                ? `[data-post-id="${id}"]`
                : `[data-comment-id="${id}"]`
        );

        if (node) {
            node.style.transition = 'opacity 160ms ease, transform 160ms ease';
            node.style.opacity = '0';
            node.style.transform = 'scale(.99)';

            window.setTimeout(() => node.remove(), 170);
        }

        return;
    }

    // Zapnout / vypnout komentáře bez redraw feedu.
    if (target === 'post' && typeof state.commentsEnabled === 'boolean') {
        const post = document.querySelector(`[data-post-id="${id}"]`);
        if (!post) return;

        const enabled = state.commentsEnabled;
        const commentsToggle = post.querySelector(`[data-comments-toggle="${id}"]`);
        const commentsHost = post.querySelector(`[data-comments-host="${id}"]`);
        const menuLabel = post.querySelector('[data-comments-toggle-label]');

        post.dataset.commentsEnabled = enabled ? '1' : '0';

        if (commentsToggle) {
            commentsToggle.dataset.commentsEnabled = enabled ? '1' : '0';
            commentsToggle.setAttribute('aria-disabled', enabled ? 'false' : 'true');
            commentsToggle.setAttribute(
                'title',
                enabled ? 'Komentáře' : 'Komentáře jsou vypnuté'
            );

            commentsToggle.classList.toggle('pointer-events-none', !enabled);
            commentsToggle.classList.toggle('opacity-40', !enabled);
            commentsToggle.classList.toggle('hover:text-slate-900', enabled);
        }

        if (commentsHost) {
            commentsHost.dataset.commentsEnabled = enabled ? '1' : '0';

            if (!enabled) {
                commentsHost.classList.add('hidden');
            }
        }

        if (menuLabel) {
            menuLabel.textContent = enabled
                ? 'Vypnout komentáře'
                : 'Zapnout komentáře';
        }

        return;
    }

    if (target === 'post' && typeof state.pinned === 'boolean') {
        const post = document.querySelector(`[data-post-id="${id}"]`);

        if (post) {
            post.dataset.pinned = state.pinned ? '1' : '0';
            post.dataset.pinnedAt = state.pinnedAt || post.dataset.pinnedAt || '';
            post.dataset.pinnedUntil = state.pinnedUntil || post.dataset.pinnedUntil || '';
            if (state.pinnedPriority !== undefined) post.dataset.pinnedPriority = String(state.pinnedPriority);

            // Připnutí už vizuálně nemění border ani pozadí celé karty.
            // Je to pouze decentní marker v pravém horním rohu hlavičky.
            post.querySelector('[data-pin-accent]')?.remove();

            const markers = post.querySelector('[data-post-markers]');
            let badge = post.querySelector('[data-pin-badge]');

            if (state.pinned) {
                if (!badge && markers) {
                    badge = document.createElement('span');
                    badge.dataset.pinBadge = '1';
                    badge.className = 'grid h-7 w-7 place-items-center text-amber-300 transition hover:text-amber-200';
                    badge.title = 'Připnutý příspěvek';
                    badge.setAttribute('aria-label', 'Připnutý příspěvek');
                    badge.innerHTML = '<i class="fa-duotone fa-solid fa-thumbtack text-sm"></i>';
                    markers.prepend(badge);
                }

            } else {
                badge?.remove();
            }

            // Důležité: přepneme i samotné menu bez reloadu.
            post.querySelectorAll('[data-pin-action-state="pinned"]').forEach(node => {
                node.classList.toggle('hidden', !state.pinned);
            });

            post.querySelectorAll('[data-pin-action-state="unpinned"]').forEach(node => {
                node.classList.toggle('hidden', state.pinned);
            });

            reorderHomepageFeed();
        }

        return;
    }

    // Nastavení ankety / prodloužení hlasování.
    if (target === 'poll') {
        let poll = document.querySelector(`[data-poll="${id}"]`);

        if (!poll && state.postId) {
            poll = document
                .querySelector(`[data-post-id="${String(state.postId)}"]`)
                ?.querySelector('[data-poll]') || null;
        }

        if (!poll) return;

        if (typeof state.allowVoteChange === 'boolean') {
            poll.dataset.pollAllowChange = state.allowVoteChange ? '1' : '0';
            updatePollVoteChangeInfo(poll, state.allowVoteChange);

            document.querySelectorAll(`[data-poll-setting-label="${id}"]`).forEach(label => {
                label.textContent = state.allowVoteChange
                    ? 'Zakázat změnu hlasu'
                    : 'Povolit změnu hlasu';
            });
        }

        if (state.endsAt) {
            poll.dataset.pollEndsAt = String(state.endsAt);
            poll.dataset.pollExpired = '0';

            updatePollDeadlineLabel(poll);
            updatePollCountdown(poll);

            poll.querySelectorAll('[data-poll-vote]').forEach(option => {
                option.classList.remove('pointer-events-none', 'opacity-50');
                option.removeAttribute('aria-disabled');
            });
        }

        return;
    }
}

function formatPinnedUntil(value) {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return '';
    }

    return new Intl.DateTimeFormat('cs-CZ', {
        day: 'numeric',
        month: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date);
}

function insertCreatedPostFromPayload(payload) {
    if (!payload?.postHtml || !payload?.postId) return;

    const feed = document.getElementById('homepage-feed');
    if (!feed) return;

    const postId = String(payload.postId);
    if (feed.querySelector(`[data-post-id="${postId}"]`)) return;

    const parsed = document.createElement('template');
    parsed.innerHTML = String(payload.postHtml).trim();
    const post = parsed.content.querySelector(`[data-post-id="${postId}"]`);
    if (!post) return;

    feed.querySelector('[data-feed-empty]')?.remove();

    const posts = Array.from(feed.querySelectorAll(':scope > [data-post-id]'));
    const firstUnpinned = posts.find(item => item.dataset.pinned !== '1');
    const tail = feed.querySelector(':scope > [data-feed-tail]');

    if (firstUnpinned) {
        feed.insertBefore(post, firstUnpinned);
    } else if (tail) {
        feed.insertBefore(post, tail);
    } else {
        feed.appendChild(post);
    }

    /*
     * Nově vytvořený post je vložený ručně z payloadu, tedy mimo klasický
     * Naja snippet redraw. Nesmíme čekat až na globální `complete` re-init,
     * protože uživatel může na komentáře kliknout okamžitě. Inicializace
     * přímo nad novým postem zajistí, že první klik funguje stejně jako u
     * postů vykreslených při načtení stránky.
     */
    initFeedUI(post);

    const visibleCount = feed.querySelectorAll(':scope > [data-post-id]').length;
    const countLabel = feed.querySelector('[data-feed-count-label]');
    if (countLabel) countLabel.textContent = `Zobrazeno ${visibleCount} příspěvků`;
}

function reorderHomepageFeed() {
    const feed = document.querySelector('#homepage-feed');
    if (!feed) return;

    const posts = Array.from(
        feed.querySelectorAll(':scope > [data-post-id]')
    );

    const tail = feed.querySelector(':scope > [data-feed-tail]');

    posts.sort((a, b) => {
        const aPinned = a.dataset.pinned === '1';
        const bPinned = b.dataset.pinned === '1';

        if (aPinned !== bPinned) {
            return aPinned ? -1 : 1;
        }

        if (aPinned && bPinned) {
            const priorityDiff = Number(a.dataset.pinnedPriority || 100) - Number(b.dataset.pinnedPriority || 100);
            if (priorityDiff !== 0) return priorityDiff;
            return Date.parse(b.dataset.pinnedAt || '') - Date.parse(a.dataset.pinnedAt || '');
        }

        return (
            Date.parse(b.dataset.publishedAt || '') -
            Date.parse(a.dataset.publishedAt || '')
        );
    });

    /*
     * "Načíst další" / "To je všechno" zůstává vždy za feedem.
     * Posty proto nevkládáme appendChild(), ale před data-feed-tail.
     */
    posts.forEach(post => {
        if (tail) {
            feed.insertBefore(post, tail);
        } else {
            feed.appendChild(post);
        }
    });
}

function closeOpenModerationDropdowns() {
    /*
     * POZOR:
     * Prefix [id^="post-menu-"] chytal i:
     *     post-menu-button-123
     * takže jsme omylem přidávali .hidden i tlačítku se třemi tečkami.
     *
     * Teď vybíráme jen skutečné dropdowny přes data atribut tlačítka,
     * resp. konkrétní ID bez "-button-".
     */
    document.querySelectorAll(
        '[id^="post-menu-"]:not([id^="post-menu-button-"]), ' +
        '[id^="comment-menu-"]:not([id^="comment-menu-button-"])'
    ).forEach(menu => {
        menu.classList.add('hidden');
    });

    document.querySelectorAll(
        '[id^="post-menu-button-"], [id^="comment-menu-button-"]'
    ).forEach(button => {
        button.setAttribute('aria-expanded', 'false');

        // Pojistka proti starému bugu:
        // kdyby tlačítko mělo hidden ze starší verze JS, okamžitě ho obnovíme.
        button.classList.remove('hidden');
    });
}
